import React, { useMemo, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  LinearProgress,
  Typography,
} from '@mui/material';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import Swal from 'sweetalert2';
import { useAtom, useSetAtom } from 'jotai';
import { notify } from '../../common/functions/react-toastify.function';
import { reviewBulkFiberPopUpload } from './functions/review_bulk_fiber_pop_upload.function';
import NavBarForUpload from '../RanDB/nav_bar_for_upload.component';
import { currentUploadStep, stepsUpload } from '../../common/atom/atoms';

const fiberPopColumns = [
  { accessorKey: 'fiber_pop', header: 'Fiber POP' },
  { accessorKey: 'latest_status', header: 'Status' },
  { accessorKey: 'ran_region', header: 'RAN Region' },
  { accessorKey: 'foo_region', header: 'FOO Region' },
  { accessorKey: 'city_name', header: 'City' },
  { accessorKey: 'type', header: 'Type' },
  { accessorKey: 'telecom_region', header: 'Telecom Region' },
  { accessorKey: 'ran_site_flag', header: 'RAN Site' },
  { accessorKey: 'ola_location_flag', header: 'OLA Location' },
  { accessorKey: 'site_name', header: 'Site Name' },
  { accessorKey: 'technology', header: 'Technology' },
  { accessorKey: 'vendor', header: 'Vendor' },
  { accessorKey: 'route', header: 'Route' },
];

const FiberPopUploadReviewModal = ({ open, onClose, previewData, onSynced }) => {
  const [currentStep, setCurrentStep] = useAtom(currentUploadStep);
  const setSteps = useSetAtom(stepsUpload);

  const newSites = useMemo(
    () => (Array.isArray(previewData?.newSites) ? previewData.newSites : []),
    [previewData]
  );
  const updatedSites = useMemo(
    () => (Array.isArray(previewData?.updatedSites) ? previewData.updatedSites : []),
    [previewData]
  );
  const deletedSites = useMemo(
    () => (Array.isArray(previewData?.deletedSites) ? previewData.deletedSites : []),
    [previewData]
  );

  // Initialize steps with counts
  useEffect(() => {
    if (open) {
      setSteps([
        `New Sites (${newSites.length})`,
        `Updated Records (${updatedSites.length})`,
        `Missing Sites (${deletedSites.length})`,
      ]);
      setCurrentStep(0);
    }
  }, [open, newSites.length, updatedSites.length, deletedSites.length, setSteps, setCurrentStep]);

  // Row selection states per step (New / Updated / Missing)
  const [newSelection, setNewSelection] = useState({});
  const [updatedSelection, setUpdatedSelection] = useState({});
  const [deletedSelection, setDeletedSelection] = useState({});
  const [isSyncing, setIsSyncing] = useState(false);

  const tableOptions = {
    enableColumnFilterModes: false,
    enableColumnOrdering: false,
    enableGrouping: false,
    enableRowSelection: true,
    enableColumnPinning: true,
    enableFacetedValues: true,
    paginationDisplayMode: 'pages',
    positionToolbarAlertBanner: 'bottom',
    enableBottomToolbar: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enablePagination: true,
    muiTableContainerProps: { sx: { maxHeight: 'calc(100vh - 320px)' } },
  };

  const newSitesTable = useMaterialReactTable({
    columns: fiberPopColumns,
    data: Array.isArray(newSites) ? newSites : [],
    state: { rowSelection: newSelection },
    onRowSelectionChange: setNewSelection,
    ...tableOptions,
  });

  const updatedSitesTable = useMaterialReactTable({
    columns: fiberPopColumns,
    data: Array.isArray(updatedSites) ? updatedSites : [],
    state: { rowSelection: updatedSelection },
    onRowSelectionChange: setUpdatedSelection,
    ...tableOptions,
  });

  const deletedSitesTable = useMaterialReactTable({
    columns: fiberPopColumns,
    data: Array.isArray(deletedSites) ? deletedSites : [],
    state: { rowSelection: deletedSelection },
    onRowSelectionChange: setDeletedSelection,
    ...tableOptions,
  });

  const handleConfirmUpload = async () => {
    const selectedNew = Object.keys(newSelection).map((key) => newSites[parseInt(key, 10)]);
    const selectedUpdated = Object.keys(updatedSelection).map(
      (key) => updatedSites[parseInt(key, 10)]
    );
    const selectedDeleted = Object.keys(deletedSelection).map(
      (key) => deletedSites[parseInt(key, 10)]
    );

    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "This will sync Fiber POP data. You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes',
      didOpen: (popup) => {
        popup.style.zIndex = 99999;
        const swalContainer = document.querySelector('.swal2-container');
        if (swalContainer) {
          swalContainer.style.zIndex = 99999;
        }
      },
    });

    if (!result.isConfirmed) {
      return;
    }

    setIsSyncing(true);
    try {
      const payload = {
        newSites: selectedNew,
        updatedSites: selectedUpdated,
        deletedSites: selectedDeleted,
      };
      const resp = await reviewBulkFiberPopUpload(payload);
      if (resp) {
        notify('success', 'Fiber POP upload synced successfully!');
        onSynced?.();
        onClose?.();
      }
    } catch (error) {
      notify('error', 'Sync failed. Please try again.');
    } finally {
      setIsSyncing(false);
    }
  };

  const handlePrimaryAction = () => {
    if (currentStep === 0) {
      setCurrentStep(1); // Add & Next
    } else if (currentStep === 1) {
      setCurrentStep(2); // Update & Next
    } else {
      handleConfirmUpload(); // Confirm Upload
    }
  };

  const primaryButtonLabel =
    currentStep === 0 ? 'Add & Next' : currentStep === 1 ? 'Update & Next' : 'Confirm Upload';

  return (
    <Dialog open={open} onClose={isSyncing ? undefined : onClose} fullWidth maxWidth="xl">
      <DialogTitle>Review Fiber POP Upload</DialogTitle>
      <DialogContent sx={{ position: 'relative' }}>
        {isSyncing && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 10,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255,255,255,0.85)',
              borderRadius: 1,
            }}
          >
            <Typography variant="h6" color="primary" sx={{ mb: 2 }}>
              Syncing in progress…
            </Typography>
            <LinearProgress sx={{ width: '80%', maxWidth: 400 }} />
          </Box>
        )}
        <NavBarForUpload />

        <Box sx={{ mt: 1 }}>
          {currentStep === 0 && <MaterialReactTable table={newSitesTable} />}
          {currentStep === 1 && <MaterialReactTable table={updatedSitesTable} />}
          {currentStep === 2 && <MaterialReactTable table={deletedSitesTable} />}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={() => setCurrentStep((prev) => Math.max(prev - 1, 0))}
          disabled={currentStep === 0 || isSyncing}
        >
          Back
        </Button>
        <Button
          variant="contained"
          sx={{ backgroundColor: '#BD1D23' }}
          onClick={handlePrimaryAction}
          disabled={
            isSyncing ||
            (currentStep === 2 &&
              Object.keys(newSelection).length === 0 &&
              Object.keys(updatedSelection).length === 0 &&
              Object.keys(deletedSelection).length === 0)
          }
        >
          {isSyncing ? 'Syncing…' : primaryButtonLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FiberPopUploadReviewModal;
